import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { AppointmentServiceRequest, FreeTimeSlot, Salon, Service } from 'api/api.types'
import { signOut } from './app.state.store'
import { getActiveServicesIndex, getSalons, getServices, type CityService } from './data.store'
import type { RootState } from './index'

/** Selected services per client: 0 is the client, further keys are companions */
export type AppointmentServices = Record<number, AppointmentServiceRequest[]>

export type AppointmentState = {
  /** Set when editing an existing visit */
  id: number | null,
  salonId: number | null,
  services: AppointmentServices,
  comment: string,
  date: string | null,
  time: string | null,
  notification: number | null,
  availableTime: FreeTimeSlot[] | null,
  availableTimeLoading: boolean,
}

export type InitAppointmentPayload = Partial<Omit<AppointmentState, 'services'>> & {
  services?: AppointmentServices | AppointmentServiceRequest[],
}

const initialState: AppointmentState = {
  id: null,
  salonId: null,
  services: { 0: [] },
  comment: '',
  date: null,
  time: null,
  notification: null,
  availableTime: null,
  availableTimeLoading: false,
}

const isOnlyEmptyClient = (services: AppointmentServices) => {
  const keys = Object.keys(services)
  return keys.length === 1 && !services[0]?.length
}

const slice = createSlice({
  name: 'appointment',
  initialState,
  reducers: {
    setAppointmentSalon: (state, action: PayloadAction<number | null>) => {
      state.salonId = action.payload
    },
    setAppointmentServices: (
      state,
      action: PayloadAction<{ clientIndex: number, services: AppointmentServiceRequest[] }>,
    ) => {
      state.services[action.payload.clientIndex] = action.payload.services
    },
    addAppointmentService: (
      state,
      action: PayloadAction<{ clientIndex: number, service: AppointmentServiceRequest }>,
    ) => {
      const { clientIndex, service } = action.payload
      const clientServices = state.services[clientIndex] ?? []
      const index = clientServices.findIndex((item) => item.id === service.id)
      if (index !== -1) {
        clientServices[index] = service
      } else {
        clientServices.push(service)
      }
      state.services[clientIndex] = clientServices
    },
    /** Swaps a selected service for another option of the same service page, keeping its place */
    replaceAppointmentService: (
      state,
      action: PayloadAction<{ clientIndex: number, serviceId: number, service: AppointmentServiceRequest }>,
    ) => {
      const { clientIndex, serviceId, service } = action.payload
      const clientServices = state.services[clientIndex] ?? []
      const index = clientServices.findIndex((item) => item.id === serviceId)
      const filtered = clientServices.filter((item, i) => i === index || item.id !== service.id)
      const newIndex = filtered.findIndex((item) => item.id === serviceId)
      if (newIndex === -1) {
        filtered.push(service)
      } else {
        filtered[newIndex] = service
      }
      state.services[clientIndex] = filtered
    },
    removeAppointmentService: (state, action: PayloadAction<{ clientIndex: number, serviceId: number }>) => {
      const { clientIndex, serviceId } = action.payload
      const current = state.services[clientIndex] ?? []
      const index = current.findIndex((item) => item.id === serviceId)
      if (index === -1) {
        return
      }
      current.splice(index, 1)
      if (isOnlyEmptyClient(state.services)) {
        state.services = { 0: [] }
      }
    },
    /** Removes a client and renumbers the rest */
    unsetAppointmentServices: (state, action: PayloadAction<{ clientIndex: number }>) => {
      const services: AppointmentServices = {}
      Object.entries(state.services).forEach(([clientIndex, selected]) => {
        if (Number(clientIndex) !== action.payload.clientIndex) {
          services[Object.keys(services).length] = selected
        }
      })
      state.services = isOnlyEmptyClient(services) || !Object.keys(services).length ? { 0: [] } : services
    },
    setAppointmentDate: (state, action: PayloadAction<string | null>) => {
      state.date = action.payload
    },
    setAppointmentTime: (state, action: PayloadAction<string | null>) => {
      state.time = action.payload
    },
    setAppointmentAvailableTime: (state, action: PayloadAction<FreeTimeSlot[] | null>) => {
      state.availableTime = action.payload
    },
    setAppointmentAvailableTimeLoading: (state, action: PayloadAction<boolean>) => {
      state.availableTimeLoading = action.payload
    },
    setAppointmentComment: (state, action: PayloadAction<string>) => {
      state.comment = action.payload
    },
    setAppointmentNotification: (state, action: PayloadAction<number | null>) => {
      state.notification = action.payload
    },
    initAppointment: (_state, action: PayloadAction<InitAppointmentPayload>) => {
      const { payload } = action
      let services: AppointmentServices = {}
      if (payload.services) {
        services = Array.isArray(payload.services) ? { 0: payload.services } : payload.services
      }
      return {
        ...initialState,
        id: payload.id ?? null,
        salonId: payload.salonId ?? null,
        services,
        date: payload.date ?? null,
        time: payload.time ?? null,
        notification: payload.notification ?? null,
        comment: payload.comment ?? '',
      }
    },
    unsetAppointment: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(signOut, () => initialState)
  },
})

export const {
  setAppointmentSalon,
  setAppointmentServices,
  addAppointmentService,
  replaceAppointmentService,
  removeAppointmentService,
  unsetAppointmentServices,
  setAppointmentDate,
  setAppointmentTime,
  setAppointmentAvailableTime,
  setAppointmentAvailableTimeLoading,
  setAppointmentComment,
  setAppointmentNotification,
  initAppointment,
  unsetAppointment,
} = slice.actions

export const appointmentReducer = slice.reducer

export const getAppointmentId = (state: RootState) => state.appointment.id
export const getAppointmentSalonId = (state: RootState) => state.appointment.salonId
export const getAppointmentTime = (state: RootState) => state.appointment.time
export const getAppointmentComment = (state: RootState) => state.appointment.comment || ''
export const getAppointmentAvailableTime = (state: RootState) => state.appointment.availableTime
export const getIsAvailableTimeLoading = (state: RootState) => state.appointment.availableTimeLoading
export const getAppointmentDate = (state: RootState) => state.appointment.date
export const getAppointmentServices = (state: RootState) => state.appointment.services
export const getAppointmentNotification = (state: RootState) => state.appointment.notification

export type SelectedService<S extends Service = Service> = S & { amount: number }

const getFullServices = <S extends Service>(
  selected: AppointmentServiceRequest[] = [],
  servicesIndex: Record<string | number, S> = {},
): Array<SelectedService<S>> => {
  return selected.reduce<Array<SelectedService<S>>>((acc, item) => {
    const service = servicesIndex[item.id]
    if (service) {
      acc.push({ ...service, amount: item.amount })
    }
    return acc
  }, [])
}

export const getAppointmentSalon = createSelector(
  [getSalons, getAppointmentSalonId],
  (salons, salonId): Salon | null => salons.find((salon) => salon.id === salonId) ?? null,
)

/** Selected services per client, resolved to full services of the current city */
export const getAppointmentServicesFull = createSelector(
  [getAppointmentServices, getActiveServicesIndex],
  (clients, servicesIndex) => {
    const result: Record<number, Array<SelectedService<CityService>>> = {}
    Object.entries(clients).forEach(([clientIndex, selected]) => {
      result[Number(clientIndex)] = getFullServices(selected, servicesIndex ?? {})
    })
    return result
  },
)

export const getAppointmentCurrentServices = (state: RootState, props: { clientIndex?: number }) => {
  return state.appointment.services[props.clientIndex ?? 0]
}

export const getAppointmentCurrentServicesFull = createSelector(
  [getAppointmentCurrentServices, getServices],
  (selected, services) => {
    const index: Record<number, Service> = {}
    services.forEach((service) => {
      index[service.id] = service
    })
    return getFullServices(selected, index)
  },
)

/** Site service types selected per client */
export const getAppointmentServicesTypes = createSelector(
  [getAppointmentServicesFull],
  (clients) => {
    const result: Record<number, number[]> = {}
    Object.entries(clients).forEach(([clientIndex, services]) => {
      const types: number[] = []
      services.forEach((service) => {
        const type = service.meta?.templateVars?.pageVars?.type
        if (type !== undefined && !types.includes(type)) {
          types.push(type)
        }
      })
      result[Number(clientIndex)] = types
    })
    return result
  },
)
